<template>
    <div class="flex-shrink-0 flex flex-col border-b lg:border-b-0 lg:border-r border-gray-200 lg:w-[350px] xl:w-[400px]"
        :class="selectedConversation ? 'hidden lg:flex' : 'flex'">
        <nav class="flex-shrink-0 p-4 space-y-4">
            <button class="btn-purple w-full" @click="$emit('new-conversation')">
                <font-awesome-icon icon="fa-plus" />
                Nova Conversa
            </button>

            <search-input v-model="searchQuery" />

            <p class="text-base font-bold">
                Todas as conversas
            </p>
        </nav>

        <ul class="flex-1 overflow-y-auto px-4 pb-4">
            <li v-for="conversation in filteredConversations" :key="conversation.id"
                @click="$emit('select', conversation)" class="cursor-pointer">
                <div class="w-full flex items-center gap-2 my-2 pr-2 cursor-pointer hover:bg-gray-100 rounded-lg p-2 transition-colors"
                    :class="conversation.unread_count > 0 ? 'bg-blue-50 border-l-4 border-blue-500 rounded-l-none' : ''">
                    <div class="w-10 h-10 flex-shrink-0 rounded-full relative pointer-events-none">
                        <img src="/images/no-photo.jpg" class="w-full rounded-full" />
                        <span class="block w-3 h-3 rounded-full absolute bottom-0 right-0 border-2 border-white"
                            :class="isOnline(conversation) ? 'bg-green-500' : 'bg-gray-400'"></span>
                        <span v-if="conversation.unread_count > 0" class="absolute -top-1 -right-1 flex h-5 w-5">
                            <span
                                class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                            <span class="relative inline-flex rounded-full h-5 w-5 bg-red-500"></span>
                        </span>
                    </div>

                    <div class="flex-1 min-w-0 pointer-events-none">
                        <p class="text-base font-bold leading-none truncate">
                            {{ getName(conversation) }}
                        </p>
                        <div class="flex justify-between items-center gap-2 mt-1">
                            <div class="text-truncate flex-1 text-sm text-gray-600 truncate markdown-preview"
                                v-html="renderMarkdown(getPreview(conversation))">
                            </div>
                            <div class="flex items-center gap-2 flex-shrink-0">
                                <p class="text-xs text-[#9C9C9C] whitespace-nowrap">
                                    {{ formatDate(conversation.last_message_at) }}
                                </p>
                                <span v-if="conversation.unread_count > 0"
                                    class="bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5 min-w-[20px] text-center shadow-lg">
                                    {{ conversation.unread_count > 99 ? '99+' : conversation.unread_count }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </li>
            <li v-if="conversations.length === 0" class="text-center text-gray-500 mt-4">
                Nenhuma conversa encontrada
            </li>
        </ul>
    </div>
</template>

<script>
import SearchInput from '@/Shared/SearchInput'
import { ref, computed } from 'vue'
import { marked } from 'marked'
import DOMPurify from 'dompurify'

// Configurar marked
marked.setOptions({
    breaks: true,
    gfm: true,
})

export default {
    components: {
        SearchInput,
    },

    props: {
        conversations: {
            type: Array,
            default: () => []
        },
        selectedConversation: {
            type: Object,
            default: null
        },
        getName: {
            type: Function,
            required: true
        },
        getPreview: {
            type: Function,
            required: true
        },
        isOnline: {
            type: Function,
            required: true
        }
    },

    emits: ['select', 'new-conversation'],

    setup(props) {
        const searchQuery = ref('')

        const filteredConversations = computed(() => {
            if (!searchQuery.value) {
                return props.conversations
            }

            return props.conversations.filter(conversation => {
                const name = props.getName(conversation)
                return name.toLowerCase().includes(searchQuery.value.toLowerCase())
            })
        })

        const formatDate = (date) => {
            if (!date) return ''

            const d = new Date(date)
            const today = new Date()

            if (d.toDateString() === today.toDateString()) {
                return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
            }

            return d.toLocaleDateString('pt-BR')
        }

        const renderMarkdown = (text) => {
            if (!text) return ''

            // Limita o texto para preview (primeiras palavras)
            const maxLength = 100
            let previewText = text
            if (text.length > maxLength) {
                previewText = text.substring(0, maxLength) + '...'
            }

            // Converte Markdown para HTML
            const html = marked.parse(previewText)

            // Sanitiza o HTML para prevenir XSS
            return DOMPurify.sanitize(html, {
                ALLOWED_TAGS: ['strong', 'em', 'u', 's', 'code'],
                ALLOWED_ATTR: []
            })
        }

        return {
            searchQuery,
            filteredConversations,
            formatDate,
            renderMarkdown
        }
    }
}
</script>

<style scoped>
/* Estilos para preview de Markdown */
.markdown-preview :deep(p) {
    margin: 0;
    padding: 0;
    display: inline;
}

.markdown-preview :deep(strong) {
    font-weight: 600;
}

.markdown-preview :deep(em) {
    font-style: italic;
}

.markdown-preview :deep(u) {
    text-decoration: underline;
}

.markdown-preview :deep(s) {
    text-decoration: line-through;
}

.markdown-preview :deep(code) {
    background-color: rgba(0, 0, 0, 0.05);
    padding: 0.125rem 0.25rem;
    border-radius: 0.25rem;
    font-family: monospace;
    font-size: 0.85em;
}

/* Remove quebras de linha no preview */
.markdown-preview :deep(br) {
    display: none;
}
</style>
